import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactForms = () => {
  const [activeForm, setActiveForm] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    phone: '',
    eventDate: '',
    audience: '',
    topic: '',
    mentorshipType: '',
    experience: '',
    goals: '',
    newsletter: false,
    terms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const contactTypes = [
    {
      id: 'general',
      title: 'General Inquiry',
      description: 'Questions, collaborations, or just saying hello',
      icon: 'MessageCircle',
      color: 'bg-accent'
    },
    {
      id: 'project',
      title: 'Project Collaboration',
      description: 'Freelance work, consulting, or development projects',
      icon: 'Code2',
      color: 'bg-success'
    },
    {
      id: 'speaking',
      title: 'Speaking Engagement',
      description: 'Conferences, workshops, podcasts, or events',
      icon: 'Mic',
      color: 'bg-warning'
    },
    {
      id: 'mentorship',
      title: 'Mentorship Request',
      description: 'Career guidance, code reviews, or learning support',
      icon: 'Users',
      color: 'bg-error'
    }
  ];

  const projectTypeOptions = [
    { value: 'web-app', label: 'Web Application' },
    { value: 'mobile-app', label: 'Mobile Application' },
    { value: 'api', label: 'API Development' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'code-review', label: 'Code Review & Audit' },
    { value: 'other', label: 'Other' }
  ];

  const budgetOptions = [
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-15k', label: '$5,000 - $15,000' },
    { value: '15k-50k', label: '$15,000 - $50,000' },
    { value: 'over-50k', label: 'Over $50,000' },
    { value: 'discuss', label: 'Let\'s Discuss' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP' },
    { value: '1-month', label: 'Within 1 Month' },
    { value: '2-3-months', label: '2-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const mentorshipTypeOptions = [
    { value: 'career', label: 'Career Guidance' },
    { value: 'technical', label: 'Technical Skills' },
    { value: 'code-review', label: 'Code Review' },
    { value: 'interview-prep', label: 'Interview Preparation' },
    { value: 'portfolio', label: 'Portfolio Review' }
  ];

  const experienceOptions = [
    { value: 'beginner', label: 'Beginner (0-1 years)' },
    { value: 'junior', label: 'Junior (1-3 years)' },
    { value: 'mid', label: 'Mid-level (3-5 years)' },
    { value: 'senior', label: 'Senior (5+ years)' },
    { value: 'student', label: 'Student' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          budget: '',
          timeline: '',
          message: '',
          phone: '',
          eventDate: '',
          audience: '',
          topic: '',
          mentorshipType: '',
          experience: '',
          goals: '',
          newsletter: false,
          terms: false
        });
        setSubmitStatus(null);
      }, 3000);
    }, 2000);
  };

  const renderFormFields = () => {
    switch (activeForm) {
      case 'project':
        return (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Your full name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                required
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@company.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                required
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Company/Organization"
                type="text"
                placeholder="Your company name"
                value={formData?.company}
                onChange={(e) => handleInputChange('company', e?.target?.value)}
              />
              <Input
                label="Phone Number"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Select
                label="Project Type"
                options={projectTypeOptions}
                value={formData?.projectType}
                onChange={(value) => handleInputChange('projectType', value)}
                placeholder="Select project type"
                required
              />
              <Select
                label="Budget Range"
                options={budgetOptions}
                value={formData?.budget}
                onChange={(value) => handleInputChange('budget', value)}
                placeholder="Select budget range"
                required
              />
              <Select
                label="Timeline"
                options={timelineOptions}
                value={formData?.timeline}
                onChange={(value) => handleInputChange('timeline', value)}
                placeholder="Select timeline"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Project Description *
              </label>
              <textarea
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none transition-all duration-300"
                rows="5"
                placeholder="Tell me about your project, goals, and any specific requirements..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                required
              />
            </div>
          </>
        );

      case 'speaking':
        return (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Your full name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                required
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@event.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                required
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Event/Organization"
                type="text"
                placeholder="Conference or organization name"
                value={formData?.company}
                onChange={(e) => handleInputChange('company', e?.target?.value)}
                required
              />
              <Input
                label="Event Date"
                type="date"
                value={formData?.eventDate}
                onChange={(e) => handleInputChange('eventDate', e?.target?.value)}
                required
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Expected Audience Size"
                type="text"
                placeholder="e.g., 50-100 developers"
                value={formData?.audience}
                onChange={(e) => handleInputChange('audience', e?.target?.value)}
              />
              <Input
                label="Preferred Topic"
                type="text"
                placeholder="React, Web Performance, etc."
                value={formData?.topic}
                onChange={(e) => handleInputChange('topic', e?.target?.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Event Details *
              </label>
              <textarea
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none transition-all duration-300"
                rows="4"
                placeholder="Tell me about the event, format (keynote, workshop, panel), and any specific topics you'd like me to cover..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                required
              />
            </div>
          </>
        );

      case 'mentorship':
        return (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Your full name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                required
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@domain.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                required
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Select
                label="Mentorship Type"
                options={mentorshipTypeOptions}
                value={formData?.mentorshipType}
                onChange={(value) => handleInputChange('mentorshipType', value)}
                placeholder="What kind of mentorship?"
                required
              />
              <Select
                label="Experience Level"
                options={experienceOptions}
                value={formData?.experience}
                onChange={(value) => handleInputChange('experience', value)}
                placeholder="Your current level"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Goals & Background *
              </label>
              <textarea
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none transition-all duration-300"
                rows="4"
                placeholder="Tell me about your background, current challenges, and what you hope to achieve through mentorship..."
                value={formData?.goals}
                onChange={(e) => handleInputChange('goals', e?.target?.value)}
                required
              />
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Your full name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                required
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@domain.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                required
              />
            </div>
            <Input
              label="Subject"
              type="text"
              placeholder="What's this about?"
              value={formData?.company}
              onChange={(e) => handleInputChange('company', e?.target?.value)}
            />
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Message *
              </label>
              <textarea
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none transition-all duration-300"
                rows="5"
                placeholder="Tell me what's on your mind..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                required
              />
            </div>
          </>
        );
    }
  };

  if (submitStatus === 'success') {
    return (
      <section id="contact-forms" className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center bg-success/10 border border-success/20 rounded-2xl p-12">
            <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={32} color="white" />
            </div>
            <h3 className="text-2xl font-bold text-success mb-4">Message Sent Successfully!</h3>
            <p className="text-text-secondary mb-6">
              Thank you for reaching out. I'll get back to you within 24 hours.
            </p>
            <Button
              variant="outline"
              onClick={() => setSubmitStatus(null)}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-forms" className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            How Can I Help You?
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Choose the type of collaboration that best fits your needs, and I'll get back to you with a personalized response.
          </p>
        </div>

        {/* Contact Type Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {contactTypes?.map((type) => (
            <button
              key={type?.id}
              onClick={() => setActiveForm(type?.id)}
              className={`p-6 rounded-xl border-2 transition-all duration-300 text-left group ${
                activeForm === type?.id
                  ? 'border-accent bg-accent/5 shadow-medium'
                  : 'border-border hover:border-accent/50 hover:bg-muted/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${type?.color} ${
                activeForm === type?.id ? 'text-white' : 'text-white group-hover:scale-110'
              } transition-transform duration-300`}>
                <Icon name={type?.icon} size={24} />
              </div>
              <h3 className={`font-semibold mb-2 ${
                activeForm === type?.id ? 'text-accent' : 'text-text-primary'
              }`}>
                {type?.title}
              </h3>
              <p className="text-sm text-text-secondary">
                {type?.description}
              </p>
            </button>
          ))}
        </div>

        {/* Contact Form */}
        <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 shadow-soft">
          <form onSubmit={handleSubmit} className="space-y-6">
            {renderFormFields()}

            {/* Checkboxes */}
            <div className="space-y-4 pt-6 border-t border-border">
              <Checkbox
                label="Subscribe to my newsletter for development tips and project updates"
                checked={formData?.newsletter}
                onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
              />
              <Checkbox
                label="I agree to the terms of service and privacy policy"
                checked={formData?.terms}
                onChange={(e) => handleInputChange('terms', e?.target?.checked)}
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isSubmitting}
                disabled={!formData?.terms}
                iconName="Send"
                iconPosition="right"
                fullWidth
                className="bg-accent hover:bg-hover text-accent-foreground"
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForms;